import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import SignatureDialog from "./SignatureDialog";
import { createEntryStateRefresh } from "./state-refresh";
import { createEntryRequestId } from "./request-id";
import EquipmentForm, {
  reconcileEquipmentDrafts,
  type EquipmentSubmission,
} from "./EquipmentForm";
import EntryHelp from "./EntryHelp";
import { ticketActivityTime } from "./TicketDecision";
import TicketActivity from "./TicketActivity";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Box,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import {
  AccountCircleOutlined,
  LockOutlined,
  Smartphone,
  Tune,
  HistoryOutlined,
  FitnessCenter,
  LogoutOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from "@mui/icons-material";
import { io, Socket } from "socket.io-client";
import { getWebSocketUrl } from "../../config";
import dico from "../../shared/util/src/language/dico";
import { formatPositionRack } from "../../shared/util/src/position-rack";
import {
  EntrySignatureDrawing,
  EntryAthlete,
  EntryCell,
  EntryReceipt,
  EntryRequest,
  isEntryValueAllowed,
  isEntryEquipmentField,
  entryDisplayValue,
} from "../../shared/util/src/athlete-entry";

type CommandState = {
  id: string;
  status: string;
  createdAt?: string;
  receipt?: EntryReceipt;
};
const duration = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
function message(error: string) {
  const t = dico().athleteEntry;
  return t.errors[error as keyof typeof t.errors] || t.error;
}
function EntryInput({
  cell,
  now,
  busy,
  pendingValue,
  awaitingReceipt,
  draft,
  onDraftChange,
  submit,
}: {
  cell: EntryCell;
  now: number;
  busy: boolean;
  pendingValue?: number;
  awaitingReceipt: boolean;
  draft?: { value: string; version: string };
  onDraftChange: (draft?: { value: string; version: string }) => void;
  submit: (r: Omit<EntryRequest, "id">) => Promise<void>;
}) {
  const t = dico().athleteEntry;
  const value = draft?.value ?? (cell.value == null ? "" : String(cell.value));
  const baseVersion = draft?.version ?? cell.version;
  const dirty = !!draft;
  const [error, setError] = useState("");
  const height = cell.kind === "height" || isEntryEquipmentField(cell.field);
  const unit = cell.unit ?? (height ? "" : "kg");
  const remaining = cell.ticket ? cell.ticket.deadline - now : null;
  const number = Number(value);
  const validValue = value.trim() !== "" && isEntryValueAllowed(cell, number);
  const rules = `${t.min}: ${cell.min}${unit ? ` ${unit}` : ""} · ${t.step}: ${cell.step}${unit ? ` ${unit}` : ""}${cell.record ? ` · ${t.recordStep}: ${cell.record.step} ${unit} (≥ ${cell.record.min} ${unit})` : ""}`;
  const rulesHelp = cell.options ? null : <EntryHelp text={rules} />;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, overflow: "hidden" }}
    >
      <Stack spacing={1.5}>
        {!height && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography component="h2" variant="subtitle1" fontWeight={600}>
              {cell.label || (t[cell.field as keyof typeof t] as string)}
            </Typography>
            {rulesHelp}
          </Stack>
        )}
        {!height &&
          !awaitingReceipt &&
          (cell.ticketCompleted ? (
            <Typography variant="body2" color="text.secondary">
              {cell.reviewRequired
                ? cell.reviewReason === "custom-attempt-change"
                  ? t.customAttemptChange
                  : t.thirdAttemptChange
                : t.ticketSubmitted}
            </Typography>
          ) : remaining === null ? (
            <Typography variant="caption" color="text.secondary">
              {t.noTimer}
            </Typography>
          ) : (
            <Box
              role="timer"
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: remaining < 0 ? "warning.main" : "divider",
                bgcolor: "action.hover",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="body2"
                  color={remaining < 0 ? "warning.main" : "text.secondary"}
                >
                  {remaining < 0 ? t.exceeded : t.remaining}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                  color={remaining < 0 ? "warning.main" : "text.primary"}
                >
                  {duration(Math.abs(remaining))}
                </Typography>
              </Stack>
              {remaining < 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t.lateNotice}
                </Typography>
              )}
            </Box>
          ))}
        <TextField
          fullWidth
          size={height ? "small" : "medium"}
          sx={{
            pt: height ? 0.5 : 0,
            ".MuiOutlinedInput-root": { borderRadius: 2 },
            ".MuiInputBase-input": {
              fontSize: height ? 16 : 32,
              fontWeight: height ? 400 : 600,
              textAlign: height ? "left" : "center",
              fontVariantNumeric: "tabular-nums",
            },
            ".MuiInputBase-input.Mui-disabled": {
              color: "text.secondary",
              WebkitTextFillColor: "currentColor",
            },
            ".MuiInputLabel-root.Mui-disabled": { color: "text.secondary" },
          }}
          label={
            height
              ? cell.label || (t[cell.field as keyof typeof t] as string)
              : `${t.weight}${unit ? ` (${unit})` : ""}`
          }
          error={dirty && !validValue}
          select={!!cell.options}
          type={cell.options ? undefined : "number"}
          value={pendingValue ?? value}
          disabled={!cell.editable || busy}
          inputProps={{
            min: Math.min(cell.min, cell.record?.min ?? cell.min),
            max: cell.max,
            step: cell.record?.step ?? cell.step,
            inputMode: "decimal",
          }}
          onChange={(e) => {
            onDraftChange({
              value: e.target.value,
              version: dirty ? baseVersion : cell.version,
            });
          }}
          helperText={
            dirty && !validValue
              ? `${t.invalidWeight}${cell.options ? "" : ` · ${rules}`}`
              : undefined
          }
        >
          {cell.options?.map((option) => (
            <MenuItem key={option.value} value={String(option.value)}>
              {cell.field === "positionRack"
                ? formatPositionRack(option.label)
                : option.label}
            </MenuItem>
          ))}
        </TextField>
        {height && (
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {!cell.editable && (
              <Chip
                icon={<LockOutlined />}
                size="small"
                variant="outlined"
                label={t.locked}
              />
            )}
            {rulesHelp}
          </Stack>
        )}
        {error && <Alert severity="error">{message(error)}</Alert>}
        {dirty && baseVersion !== cell.version && (
          <Alert severity="warning">
            {t.draftChanged}
            <Typography variant="body2">
              {t.current}: {cell.value ?? "-"} · {t.proposed}: {value}
            </Typography>
            <Button
              disabled={busy}
              onClick={() => {
                onDraftChange({ value, version: cell.version });
                setError("");
              }}
            >
              {t.useCurrentRules}
            </Button>
          </Alert>
        )}
        {cell.editable && (
          <Button
            variant="contained"
            disableElevation
            fullWidth
            sx={{
              minHeight: height ? 40 : 46,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
            disabled={
              busy ||
              !cell.editable ||
              !dirty ||
              !validValue ||
              (!!cell.ticketCompleted && number === cell.value) ||
              baseVersion !== cell.version
            }
            onClick={async () => {
              setError("");
              try {
                await submit({
                  field: cell.field,
                  value: Number(value),
                  version: baseVersion,
                });
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            {t.signAndSend}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
export default function AthleteEntryPage() {
  const { meetId = "" } = useParams<{ meetId: string }>();
  const t = dico().athleteEntry;
  const [info, setInfo] = useState<{
    name: string;
    enabled: boolean;
    online: boolean;
  }>();
  const [drafts, setDrafts] = useState<
    Record<string, { value: string; version: string }>
  >({});
  const submittedDrafts = useRef(new Map<string, EquipmentSubmission>());
  const [failedRequest, setFailedRequest] = useState<{
    request: EntryRequest;
    reason: string;
  }>();
  const [athlete, setAthlete] = useState<EntryAthlete>();
  const [personalCode, setPersonalCode] = useState("");
  const [revealedCode, setRevealedCode] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [signaturePrompt, setSignaturePrompt] = useState<{
    summary: string;
    complete: (drawing?: EntrySignatureDrawing) => void;
  }>();
  const signaturePromptRef = useRef(signaturePrompt);
  signaturePromptRef.current = signaturePrompt;
  useEffect(() => () => signaturePromptRef.current?.complete(), [athlete?.id]);
  const [section, setSection] = useState<
    "attempts" | "equipment" | "history" | null
  >(null);
  const athleteRef = useRef(athlete);
  athleteRef.current = athlete;
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [online, setOnline] = useState(false);
  const [working, setWorking] = useState(false);
  const [pending, setPending] = useState<EntryRequest | null>(() => {
    try {
      return JSON.parse(
        localStorage.getItem(`entry-pending:${meetId}`) || "null",
      );
    } catch {
      return null;
    }
  });
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const [unconfirmed, setUnconfirmed] = useState(
    () => sessionStorage.getItem(`entry-unconfirmed:${meetId}`) || "",
  );
  const [uncertain, setUncertain] = useState(!!pending);
  const [commands, setCommands] = useState<CommandState[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<
    Record<string, string>
  >({});
  const [now, setNow] = useState(Date.now());
  const clockOffset = useRef(0);
  const socket = useRef<Socket>();
  const stateRefresh = useRef<() => void>(() => {});
  const lastStateAt = useRef(0);
  const expireSession = useCallback(() => {
    if (pendingRef.current) {
      sessionStorage.setItem(
        `entry-unconfirmed:${meetId}`,
        pendingRef.current.id,
      );
      setUnconfirmed(pendingRef.current.id);
    }
    signaturePromptRef.current?.complete();
    setSignaturePrompt(undefined);
    setHasSignature(false);
    setAthlete(undefined);
    setPersonalCode("");
    setRevealedCode("");
    setFailedRequest(undefined);
    setDrafts({});
    submittedDrafts.current.clear();
    setOnline(false);
    setError("ENTRY_SESSION_EXPIRED");
    localStorage.removeItem(`entry-pending:${meetId}`);
    setPending(null);
    pendingRef.current = null;
    setUncertain(false);
  }, [meetId]);
  const api = `${getWebSocketUrl()}/athlete-entry/${encodeURIComponent(meetId)}`;
  const refresh = useCallback(() => stateRefresh.current(), []);
  useEffect(() => {
    let alive = true;
    fetch(api, { credentials: "include", cache: "no-store" })
      .then(async (r) => {
        const b = await r.json();
        if (!r.ok) throw Error(b.message);
        if (alive) setInfo(b);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      });
    const s = io(`${getWebSocketUrl()}/athlete-entry`, {
      auth: { meetId },
      withCredentials: true,
      transports: ["websocket"],
    });
    socket.current = s;
    const updates = createEntryStateRefresh(
      () =>
        new Promise<{
          sent: number;
          connectionId: string | undefined;
          response: any;
        }>((resolve, reject) => {
          const sent = Date.now();
          const connectionId = s.id;
          if (!s.connected) return reject(Error("DESKTOP_OFFLINE"));
          s.timeout(8000).emit(
            "state",
            { commandId: pendingRef.current?.id },
            (error: Error, response: any) => {
              if (error) reject(error);
              else resolve({ sent, connectionId, response });
            },
          );
        }),
      ({ sent, connectionId, response }) => {
        if (!s.connected || s.id !== connectionId) return;
        if (!response?.ok) {
          if (
            response?.error === "ENTRY_SESSION_EXPIRED" ||
            response?.error === "ATHLETE_UNAVAILABLE"
          ) {
            expireSession();
            return;
          }
          setError(response?.error || "DESKTOP_OFFLINE");
          setOnline(false);
          if (pendingRef.current) setUncertain(true);
          return;
        }
        const state = response.data;
        clockOffset.current = state.serverNow - (sent + Date.now()) / 2;
        lastStateAt.current = Date.now();
        setOnline(true);
        setError("");
        setHasSignature(!!state.hasSignature);
        setAthlete(state.athlete);
        setPersonalCode(state.code ?? "");
        setCommands(state.commands);
        for (const command of state.commands as CommandState[]) {
          const submission = submittedDrafts.current.get(command.id);
          if (!submission || ["pending", "executing"].includes(command.status))
            continue;
          setDrafts((current) =>
            reconcileEquipmentDrafts(current, submission, command),
          );
          submittedDrafts.current.delete(command.id);
        }
        const p = pendingRef.current;
        if (
          p &&
          state.commands.some(
            (c: CommandState) =>
              c.id === p.id && !["pending", "executing"].includes(c.status),
          )
        ) {
          localStorage.removeItem(`entry-pending:${meetId}`);
          setPending(null);
          pendingRef.current = null;
          setUncertain(false);
        }
      },
      () => {
        setOnline(false);
        if (pendingRef.current) setUncertain(true);
      },
    );
    const requestRefresh = () => {
      void updates.refresh();
    };
    stateRefresh.current = requestRefresh;
    lastStateAt.current = 0;
    let lastRecoveryAt = 0;
    s.on("ready", refresh);
    s.on("changed", refresh);
    s.on("disconnect", () => {
      setOnline(false);
      if (pendingRef.current) setUncertain(true);
    });
    s.on("connect_error", () => {
      setOnline(false);
    });
    s.on("entry-error", (reason: string) => {
      setOnline(false);
      if (
        reason === "ENTRY_SESSION_EXPIRED" ||
        reason === "ATHLETE_UNAVAILABLE"
      ) {
        // Do not show an expiry warning on the initial anonymous connection.
        if (athleteRef.current || pendingRef.current) expireSession();
      } else setError(reason || "DESKTOP_OFFLINE");
    });
    const timer = window.setInterval(() => {
      setNow(Date.now() + clockOffset.current);
      // Recover from a missed push or transient state error while the page stays open.
      if (
        !document.hidden &&
        s.connected &&
        Date.now() - lastStateAt.current > 5000 &&
        Date.now() - lastRecoveryAt >= 5000
      ) {
        lastRecoveryAt = Date.now();
        refresh();
      }
      if (Date.now() - lastStateAt.current > 15000) {
        setOnline(false);
        if (pendingRef.current) setUncertain(true);
      }
    }, 250);
    const foreground = () => {
      if (!document.hidden) {
        if (!s.connected) s.connect();
        else refresh();
      }
    };
    document.addEventListener("visibilitychange", foreground);
    return () => {
      alive = false;
      updates.dispose();
      if (stateRefresh.current === requestRefresh)
        stateRefresh.current = () => {};
      s.disconnect();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [api, meetId, refresh, expireSession]);
  const send = (request: EntryRequest) =>
    new Promise<void>((resolve, reject) => {
      const s = socket.current;
      if (!s?.connected || !online) return reject(Error("DESKTOP_OFFLINE"));
      setFailedRequest(undefined);
      if (!submittedDrafts.current.has(request.id)) {
        const sent = Object.fromEntries(
          [request, ...(request.updates ?? [])]
            .filter(
              (update) =>
                drafts[update.field]?.version === update.version &&
                Number(drafts[update.field]?.value) === update.value,
            )
            .map((update) => [update.field, drafts[update.field]]),
        );
        submittedDrafts.current.set(request.id, {
          id: request.id,
          drafts: sent,
        });
      }
      setPending(request);
      pendingRef.current = request;
      setUncertain(false);
      localStorage.setItem(`entry-pending:${meetId}`, JSON.stringify(request));
      s.timeout(10000).emit("command", request, (err: Error, response: any) => {
        if (err) {
          setUncertain(true);
          return resolve();
        }
        if (!response?.ok) {
          localStorage.removeItem(`entry-pending:${meetId}`);
          setPending(null);
          pendingRef.current = null;
          setFailedRequest({
            request,
            reason: response?.error || "INVALID_REQUEST",
          });
          refresh();
          return reject(Error(response?.error || "INVALID_REQUEST"));
        }
        refresh();
        resolve();
      });
    });
  const signAndSend = async (request: EntryRequest) => {
    let drawing: EntrySignatureDrawing | undefined;
    if (!hasSignature) {
      const summary = [request, ...(request.updates || [])]
        .map((update) => {
          const cell = athlete?.cells.find((c) => c.field === update.field);
          return `${cell?.label || (t[update.field as keyof typeof t] as string) || update.field} : ${entryDisplayValue(update.field, update.value, cell?.options?.find((o) => o.value === update.value)?.label)}${cell?.unit ? ` ${cell.unit}` : cell?.kind === "attempt" ? " kg" : ""}`;
        })
        .join(" · ");
      drawing = await new Promise<EntrySignatureDrawing | undefined>(
        (resolve) => setSignaturePrompt({ summary, complete: resolve }),
      );
      setSignaturePrompt(undefined);
      if (!drawing) throw Error("SIGNATURE_CANCELLED");
    }
    await send({
      ...request,
      confirmSignature: true,
      ...(drawing ? { signatureDrawing: drawing } : {}),
    });
  };
  const heightCell = (c: EntryCell) =>
    c.kind === "height" || isEntryEquipmentField(c.field);
  const phase =
    athlete?.phase ||
    (athlete?.cells.some((c) => !heightCell(c) && c.editable)
      ? "attempts"
      : "heights");
  const declaredCell = athlete?.cells.find((c) => c.next && c.ticketCompleted);
  const remotePending =
    !pending &&
    commands.some((c) => ["pending", "executing"].includes(c.status));
  const history = commands
    .filter((c) => !["pending", "executing"].includes(c.status))
    .sort((a, b) => ticketActivityTime(b) - ticketActivityTime(a));
  const activeSection =
    section ?? (phase === "heights" ? "equipment" : "attempts");
  const heightsScreen = activeSection === "equipment";
  const latestDecision = history.find((command) => command.receipt?.decision);
  const priorityNotifications = history.filter(
    (command, index) =>
      index === 0 ||
      command === latestDecision ||
      (command.receipt?.status === "applied" &&
        !command.receipt.decision &&
        !command.receipt.superseded &&
        (["late", "unknown"].includes(command.receipt.timing) ||
          command.receipt.reviewRequired)),
  );
  const availableNotifications = (
    priorityNotifications.length ? priorityNotifications : history
  ).filter(
    (command) => dismissedNotifications[command.id] !== JSON.stringify(command),
  );
  const notifications = availableNotifications.slice(0, 1);
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "action.hover" }}>
      {signaturePrompt && (
        <SignatureDialog
          summary={signaturePrompt.summary}
          onConfirm={(drawing) => signaturePrompt.complete(drawing)}
          onCancel={() => signaturePrompt.complete()}
        />
      )}
      <Container
        maxWidth="sm"
        sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 0.5 }}
          >
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 2,
                color: "primary.main",
                bgcolor: "background.paper",
              }}
            >
              {athlete ? (
                <AccountCircleOutlined />
              ) : (
                <Smartphone fontSize="small" />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {!athlete && (
                <Typography variant="caption" color="text.secondary">
                  {t.title}
                </Typography>
              )}
              <Typography
                component="h1"
                variant="h6"
                fontWeight={600}
                sx={{ lineHeight: 1.3, overflowWrap: "anywhere" }}
              >
                {athlete?.name || info?.name || t.title}
              </Typography>
              {athlete && (
                <Typography variant="body2" color="text.secondary">
                  {athlete.groupName}
                </Typography>
              )}
              {athlete && personalCode && (
                <Button
                  size="small"
                  aria-label={
                    revealedCode === personalCode
                      ? `${t.code}: ${personalCode.split("").join(" ")}. ${t.hideCode}`
                      : t.showCode
                  }
                  startIcon={
                    revealedCode === personalCode ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )
                  }
                  onClick={() =>
                    setRevealedCode((current) =>
                      current === personalCode ? "" : personalCode,
                    )
                  }
                  sx={{
                    px: 0,
                    minWidth: 0,
                    textTransform: "none",
                    ...(revealedCode === personalCode
                      ? { fontFamily: "monospace", letterSpacing: "0.12em" }
                      : {}),
                  }}
                >
                  {revealedCode === personalCode ? personalCode : t.code}
                </Button>
              )}
            </Box>
            {athlete && (
              <IconButton
                aria-label={t.disconnect}
                size="small"
                onClick={async () => {
                  try {
                    const r = await fetch(`${api}/logout`, {
                      method: "POST",
                      credentials: "include",
                    });
                    if (!r.ok) throw Error("ENTRY_SESSION_EXPIRED");
                    socket.current?.disconnect();
                    expireSession();
                    setError("");
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                <LogoutOutlined fontSize="small" />
              </IconButton>
            )}
          </Stack>
          {error && <Alert severity="error">{message(error)}</Alert>}
          {unconfirmed && (
            <Alert severity="warning">
              {t.unconfirmedAfterExpiry}
              <Button
                size="small"
                onClick={() => {
                  sessionStorage.removeItem(`entry-unconfirmed:${meetId}`);
                  setUnconfirmed("");
                }}
              >
                {t.checkedWithSecretary}
              </Button>
            </Alert>
          )}
          {info && !info.enabled && <Alert severity="info">{t.disabled}</Alert>}
          {!athlete ? (
            <Paper
              variant="outlined"
              sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3 }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h6" fontWeight={600}>
                    {t.connect}
                  </Typography>
                  <EntryHelp text={t.twoPhones} />
                </Stack>
                <TextField
                  fullWidth
                  sx={{
                    ".MuiOutlinedInput-root": { borderRadius: 2 },
                    ".MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: 24,
                      letterSpacing: ".15em",
                      textAlign: "center",
                    },
                  }}
                  label={t.code}
                  value={code}
                  autoComplete="off"
                  inputProps={{
                    maxLength: 12,
                    autoCapitalize: "characters",
                    spellCheck: false,
                  }}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\s/g, "").toUpperCase())
                  }
                />
                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    minHeight: 46,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                  disabled={
                    working ||
                    info?.enabled === false ||
                    !/^[0-9A-F]{6}$/.test(code.replace(/\s/g, ""))
                  }
                  onClick={async () => {
                    setWorking(true);
                    setError("");
                    try {
                      const r = await fetch(`${api}/login`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ code }),
                      });
                      const body = await r.json();
                      if (!r.ok) throw Error(body.message || "INVALID_CODE");
                      setCode("");
                      socket.current?.disconnect();
                      socket.current?.connect();
                    } catch (e) {
                      setError((e as Error).message);
                    } finally {
                      setWorking(false);
                    }
                  }}
                >
                  {working ? <CircularProgress size={20} /> : t.connect}
                </Button>
              </Stack>
            </Paper>
          ) : (
            <>
              {notifications.length > 0 && (
                <Box component="section" aria-label={t.notifications}>
                  <Stack spacing={1}>
                    {notifications.map((command) => (
                      <TicketActivity
                        key={command.id}
                        command={command}
                        cells={athlete.cells}
                        onClose={() =>
                          setDismissedNotifications((previous) => ({
                            ...previous,
                            [command.id]: JSON.stringify(command),
                          }))
                        }
                      />
                    ))}
                    {availableNotifications.length > 1 &&
                      activeSection !== "history" && (
                        <Button
                          size="small"
                          onClick={() => setSection("history")}
                          sx={{ alignSelf: "flex-end", textTransform: "none" }}
                        >
                          {t.historyTab} · +{availableNotifications.length - 1}
                        </Button>
                      )}
                  </Stack>
                </Box>
              )}
              {Object.entries(drafts)
                .filter(
                  ([field]) =>
                    !athlete.cells.find((c) => c.field === field)?.editable &&
                    ![
                      ...(pending ? [pending, ...(pending.updates || [])] : []),
                    ].some((update) => update.field === field),
                )
                .map(([field, draft]) => (
                  <Alert key={field} severity="warning">
                    {t.draftNotSent}
                    <Typography variant="body2">
                      {t.current}:{" "}
                      {athlete.cells.find((c) => c.field === field)?.value ??
                        "-"}{" "}
                      · {t.proposed}: {draft.value}
                    </Typography>
                    <Button
                      onClick={() =>
                        setDrafts((old) => {
                          const next = { ...old };
                          delete next[field];
                          return next;
                        })
                      }
                    >
                      {t.close}
                    </Button>
                  </Alert>
                ))}
              {failedRequest && (
                <Alert severity="error">
                  {message(failedRequest.reason)}
                  {[
                    failedRequest.request,
                    ...(failedRequest.request.updates || []),
                  ].map((update) => (
                    <Typography key={update.field} variant="body2">
                      {athlete.cells.find((cell) => cell.field === update.field)
                        ?.label ||
                        (t[update.field as keyof typeof t] as string)}{" "}
                      · {t.current}:{" "}
                      {entryDisplayValue(
                        update.field,
                        athlete.cells.find(
                          (cell) => cell.field === update.field,
                        )?.value,
                      )}{" "}
                      · {t.proposed}:{" "}
                      {entryDisplayValue(update.field, update.value)}
                    </Typography>
                  ))}
                  <Button onClick={() => setFailedRequest(undefined)}>
                    {t.close}
                  </Button>
                </Alert>
              )}
              {!online && <Alert severity="warning">{t.offline}</Alert>}
              {remotePending && (
                <Alert severity="info">{t.otherPhonePending}</Alert>
              )}
              {pending && (
                <Alert severity={uncertain ? "warning" : "info"}>
                  {uncertain ? t.uncertain : t.pending}
                  {uncertain && (
                    <Button
                      onClick={() => {
                        refresh();
                      }}
                    >
                      {" "}
                      {t.refresh}
                    </Button>
                  )}
                </Alert>
              )}
              {pending && uncertain && online && (
                <Button
                  onClick={() =>
                    void send(pending).catch((e) => setError(e.message))
                  }
                >
                  {t.retry}
                </Button>
              )}
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, position: "sticky", top: 8, zIndex: 2 }}
              >
                <Tabs
                  value={activeSection}
                  onChange={(_, value) => setSection(value)}
                  variant="fullWidth"
                  aria-label={t.title}
                  sx={{
                    minHeight: 52,
                    "& .MuiTab-root": {
                      minWidth: 0,
                      px: 0.5,
                      minHeight: 52,
                      textTransform: "none",
                      fontSize: 13,
                    },
                  }}
                >
                  <Tab
                    value="attempts"
                    icon={<FitnessCenter fontSize="small" />}
                    iconPosition="start"
                    label={t.attemptsTab}
                  />
                  <Tab
                    value="equipment"
                    icon={<Tune fontSize="small" />}
                    iconPosition="start"
                    label={t.equipmentTab}
                  />
                  <Tab
                    value="history"
                    icon={<HistoryOutlined fontSize="small" />}
                    iconPosition="start"
                    label={t.historyTab}
                  />
                </Tabs>
              </Paper>
              {activeSection === "history" && (
                <Stack spacing={1.5} component="section" aria-label={t.tickets}>
                  {history.length ? (
                    history.map((command) => (
                      <TicketActivity
                        key={command.id}
                        command={command}
                        cells={athlete.cells}
                      />
                    ))
                  ) : (
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ py: 2 }}
                    >
                      {t.noTickets}
                    </Typography>
                  )}
                </Stack>
              )}
              {activeSection === "attempts" &&
                athlete.passagesBefore !== undefined && (
                  <Alert severity="info">
                    <Typography fontWeight="bold">
                      {dico().ranking.passageStatus(athlete.passagesBefore)}
                    </Typography>
                  </Alert>
                )}
              {activeSection === "attempts" &&
                !athlete.cells.some(
                  (cell) =>
                    cell.editable &&
                    cell.kind !== "height" &&
                    !isEntryEquipmentField(cell.field),
                ) && (
                  <Alert severity="info">
                    {athlete.attemptState === "finished"
                      ? t.finished
                      : athlete.attemptState === "unavailable"
                        ? t.groupContextUnavailable
                        : athlete.cells.some(
                              (cell) => cell.next && cell.entryRefused,
                            )
                          ? t.refusedAttemptClosed
                          : athlete.attemptState === "submitted"
                            ? t.waitingResult
                            : athlete.attemptState === "on-bar"
                              ? t.onBar
                              : t.noAttemptAvailable}
                    {athlete.attemptState !== "finished" &&
                      athlete.attemptState !== "unavailable" &&
                      (athlete.attemptState === "submitted" ||
                        athlete.cells.some(
                          (cell) => cell.next && cell.entryRefused,
                        )) &&
                      declaredCell && (
                        <Typography variant="body2">
                          {t.current}: {declaredCell.value}{" "}
                          {declaredCell.unit ?? "kg"}
                        </Typography>
                      )}
                  </Alert>
                )}
              <Box hidden={!heightsScreen}>
                <EquipmentForm
                  key={athlete.id}
                  requireSignature={!hasSignature}
                  cells={athlete.cells.filter(heightCell)}
                  drafts={drafts}
                  onDraftsChange={setDrafts}
                  busy={
                    !online ||
                    !!unconfirmed ||
                    !!pending ||
                    remotePending ||
                    !!signaturePrompt
                  }
                  pending={pending}
                  outcomes={commands}
                  submit={async (request) => {
                    const id = createEntryRequestId();
                    await signAndSend({ ...request, id });
                    return id;
                  }}
                />
              </Box>
              {activeSection === "attempts" &&
                athlete.cells
                  .filter((cell) => !heightCell(cell) && cell.editable)
                  .map((cell) => (
                    <EntryInput
                      key={cell.field}
                      cell={cell}
                      draft={drafts[cell.field]}
                      onDraftChange={(draft) =>
                        setDrafts((old) => {
                          const next = { ...old };
                          if (draft) next[cell.field] = draft;
                          else delete next[cell.field];
                          return next;
                        })
                      }
                      awaitingReceipt={!!pending || remotePending}
                      pendingValue={
                        pending?.field === cell.field
                          ? pending.value
                          : undefined
                      }
                      now={now}
                      busy={
                        !online ||
                        !!signaturePrompt ||
                        !!unconfirmed ||
                        !!pending ||
                        commands.some((c) =>
                          ["pending", "executing"].includes(c.status),
                        )
                      }
                      submit={(r) =>
                        signAndSend({ ...r, id: createEntryRequestId() })
                      }
                    />
                  ))}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
